import React from 'react'
import { useState } from 'react'
import {
    Box,
    Typography,
    Card,
    CardContent,
    FormControlLabel,
    Button,
    Checkbox,
} from '@mui/material'
import { RoundedInputField } from '@/shared/ui/RoundedInputField'
import { useTranslation } from 'react-i18next'
import { postWithAuth } from '@/api/httpRequests'
import { newsletterSubscribe } from '@/api/apiConstants'
import {
    buttonStyle,
    buyButtonStyle,
} from '@/features/VOD/components/itemDetails/styles/detailsButtonsStyles'

const NewsletterSection: React.FC = () => {
    const { t: translate } = useTranslation()

    const [email, setEmail] = useState<string>('')
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null)
    const [feedbackType, setFeedbackType] = useState<
        'success' | 'error' | null
    >(null)
    const [isConsentChecked, setIsConsentChecked] = useState<boolean>(false)

    const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(email)
    }

    const inputFieldStyle = {
        background: 'none',
        width: '50%',
        padding: '10px',
        borderRadius: '8px',
        border: '1px solid grey',
        color: 'white',
        margin: '0 auto',
        marginTop: '5px',
        outline: 'none',
    }

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()
        setFeedbackMessage(null)

        if (!email) {
            setFeedbackMessage(translate('emailRequired'))
            setFeedbackType('error')
            return
        }

        if (!isValidEmail(email)) {
            setFeedbackMessage(translate('emailInvalid'))
            setFeedbackType('error')
            return
        }

        if (!isConsentChecked) {
            setFeedbackMessage(translate('newsletterConsentRequired'))
            setFeedbackType('error')
            return
        }

        setIsSubmitting(true)

        try {
            const body = JSON.stringify({ email })
            const response = await postWithAuth(newsletterSubscribe, true, body)

            if (response.result === 'ok') {
                setEmail('')
                setFeedbackMessage(translate('newsletterSubscriptionSuccess'))
                setFeedbackType('success')
            } else {
                setFeedbackMessage(translate('newsletterSubscriptionError'))
                setFeedbackType('error')
            }
        } catch (error) {
            setFeedbackMessage(translate('newsletterSubscriptionError'))
            setFeedbackType('error')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Box sx={{ marginTop: '2rem' }}>
            <Card
                sx={{
                    margin: '0 auto',
                    padding: '1.5rem',
                    backgroundColor: '#3D3D3D',
                    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
                }}>
                <CardContent>
                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{ color: 'white' }}>
                        {translate('vodNewsletterJoin')}
                    </Typography>
                    <Typography
                        variant="body1"
                        gutterBottom
                        sx={{ color: 'white', marginBottom: '1rem' }}>
                        Co tydzień do wszystkich naszych bibliotek VOD trafia
                        kilkaset pozycji. Aby ułatwić wyszukiwanie i
                        przeglądanie zawartości oraz śledzenie nowości,
                        uruchomiliśmy poniższą stronę. Cotygodniowe informacje o
                        wszystkich nowościach znajdują się w informatorze, który
                        można zaprenumerować wypełniając ten formularz:
                    </Typography>
                    <form onSubmit={handleSubmit}>
                        <RoundedInputField
                            searchValue={email}
                            setSearchValue={setEmail}
                            placeholder="email"
                            native={{
                                autoComplete: 'off',
                                type: 'text',
                                name: 'email',
                                maxLength: 100,
                                size: 100,
                            }}
                            sx={inputFieldStyle}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isConsentChecked}
                                    onChange={(event) =>
                                        setIsConsentChecked(
                                            event.target.checked
                                        )
                                    }
                                />
                            }
                            sx={{ marginTop: '1rem' }}
                            label={
                                <Typography
                                    variant="body2"
                                    sx={{ color: 'white' }}>
                                    Wyrażam zgodę na przetwarzanie moich danych
                                    osobowych, w tym podanego przeze mnie adresu
                                    poczty elektronicznej, przez TOYA sp. z o.o.
                                    z siedzibą w Łodzi przy ul. Łąkowej 29
                                    (zwanej dalej Spółką), w celu wysyłania
                                    informacji handlowych za pomocą środków
                                    komunikacji elektronicznej. Dane
                                    przetwarzane będą zgodnie z Ustawą o
                                    ochronie danych osobowych z dnia 29 sierpnia
                                    1997 roku tekst jednolity Dz.U. z 2002r., Nr
                                    101, poz.926, z późn. zm.). Podanie danych
                                    jest dobrowolne, przysługuje prawo kontroli
                                    przetwarzania danych, prawo dostępu do
                                    treści swoich danych i ich poprawiania.
                                </Typography>
                            }
                        />
                        <Button
                            type="submit"
                            disabled={isSubmitting}
                            sx={{
                                ...buttonStyle,
                                ...buyButtonStyle,
                                marginTop: '1rem',
                            }}>
                            Zapisz się
                        </Button>
                        {feedbackMessage && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color:
                                        feedbackType === 'success'
                                            ? 'lightgreen'
                                            : 'salmon',
                                    marginTop: '1rem',
                                }}>
                                {feedbackMessage}
                            </Typography>
                        )}
                    </form>
                </CardContent>
            </Card>
        </Box>
    )
}

export { NewsletterSection }
